import React, { memo, useMemo } from 'react';
import {
  Stack,
  Typography,
  CardHeader,
  CardContent,
  Card,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Icon,
  TableContainer,
  useMediaQuery
} from 'investira.react.components';
import { validators } from 'investira.sdk';
import { MelhorValorTableCell, PessoaImage } from '../../../components/molecules';
import comparisons from '../../../utils/comparisons';
import utils from '../../../utils';

const wMetricas = [
  { key: 'mesesPositivos', criterion: 'higher' },
  { key: 'mesesNegativos', criterion: 'lower' },
  { key: 'maiorRetorno', criterion: 'higher' },
  { key: 'menorRetorno', criterion: 'lower' }
];

/** Calcula os retornos mensais já usados na página individual. */
export const analisarRetornos = pDados => {
  if (validators.isNull(pDados)) return {};
  const xDadosPorMes = pDados.reduce((pAcumulado, pItem) => {
    const xChaveMes = pItem.data.substring(0, 7);
    if (!pAcumulado[xChaveMes] || new Date(pItem.data) > new Date(pAcumulado[xChaveMes].data))
      pAcumulado[xChaveMes] = pItem;
    return pAcumulado;
  }, {});
  let xMesesPositivos = 0;
  let xMesesNegativos = 0;
  let xMaiorRetorno = -Infinity;
  let xMenorRetorno = Infinity;
  const xMeses = Object.values(xDadosPorMes).sort(
    (pPrimeiro, pSegundo) => new Date(pPrimeiro.data) - new Date(pSegundo.data)
  );
  for (let xIndex = 1; xIndex < xMeses.length; xIndex += 1) {
    let xRetorno = xMeses[xIndex].cotacao_ajustada / xMeses[xIndex - 1].cotacao_ajustada - 1;
    xRetorno = isNaN(xRetorno) || xRetorno === Infinity ? 0 : xRetorno;
    if (xRetorno > 0) xMesesPositivos += 1;
    if (xRetorno < 0) xMesesNegativos += 1;
    xMaiorRetorno = Math.max(xMaiorRetorno, xRetorno);
    xMenorRetorno = Math.min(xMenorRetorno, xRetorno);
  }
  const xTotal = xMesesPositivos + xMesesNegativos;
  return {
    mesesPositivos: xMesesPositivos,
    mesesNegativos: xMesesNegativos,
    maiorRetorno: Number.isFinite(xMaiorRetorno) ? xMaiorRetorno * 100 : null,
    menorRetorno: Number.isFinite(xMenorRetorno) ? xMenorRetorno * 100 : null,
    percentualMesesPositivos: `${
      !isNaN((xMesesPositivos / xTotal) * 100) ? ((xMesesPositivos / xTotal) * 100).toFixed(2) : 0
    }%`,
    percentualMesesNegativos: `${
      !isNaN((xMesesNegativos / xTotal) * 100) ? ((xMesesNegativos / xTotal) * 100).toFixed(2) : 0
    }%`
  };
};

/** Apresenta a consistência mensal de uma lista ordenada de ativos. */
const AtivoConsistencia = memo(({ ativos = [], cotacoesPorId = {}, highlightEnabled = false }) => {
  const xAtivos = useMemo(() => ativos.filter(pAtivo => pAtivo?.ifeed_ativo_id), [ativos]);
  const isUpSm = useMediaQuery(pTheme => pTheme.breakpoints.up('sm'));

  const xSeries = xAtivos
    .map(xAtivo => {
      const xAtivoId = xAtivo.ifeed_ativo_id;
      const xCotacao = cotacoesPorId[xAtivoId];

      if (!xCotacao || xCotacao.loading) {
        return null;
      }

      return {
        ativo: xAtivo.ticker || xAtivo.ativo_seuid || xAtivo.ativo || String(xAtivoId),
        resultado: validators.isEmpty(xCotacao.data) ? null : analisarRetornos(xCotacao.data),
        raw: xAtivo
      };
    })
    .filter(Boolean);
  if (validators.isEmpty(xSeries)) return null;
  const xBestIndexes = wMetricas.reduce(
    (pBestIndexes, pMetrica) => ({
      ...pBestIndexes,
      [pMetrica.key]: comparisons.getBestValueIndexes(
        xSeries.map(pSerie => pSerie.resultado?.[pMetrica.key]),
        pMetrica.criterion
      )
    }),
    {}
  );

  const pvSemDado = () => (
    <Tooltip title="Sem dado" arrow>
      <Box component="span" tabIndex={0} sx={{ display: 'inline-flex' }}>
        <Typography component="span" variant="caption" color="textSecondary" fontWeight="bold">
          -
        </Typography>
      </Box>
    </Tooltip>
  );

  return (
    <Stack flexGrow={1}>
      <Card component="section">
        <CardHeader
          title={
            <Stack direction="row" gap={1} alignItems="center">
              <Typography
                variant="subtitle1"
                component="h3"
                color="textPrimary"
                sx={{ fontWeight: 600 }}
                noWrap>
                Consistência
              </Typography>
              <Tooltip
                title={
                  <Stack p={1}>
                    <Typography variant="caption" color="textPrimary">
                      <b>Meses Positivos:</b> quantos meses o ativo se valorizou.{' '}
                    </Typography>
                    <Typography variant="caption" color="textPrimary">
                      <b>Meses Negativos:</b> quantos meses se desvalorizou.
                    </Typography>
                    <Typography variant="caption" color="textPrimary">
                      <b>Maior e menor retorno:</b> extremos mensais.
                    </Typography>
                  </Stack>
                }
                arrow>
                <span>
                  <Icon iconName="information" size={16} color="primary" />
                </span>
              </Tooltip>
            </Stack>
          }
        />
        <CardContent>
          <Stack sx={{ pt: 0 }}>
            <TableContainer id="table-container" sx={{ overflow: 'auto' }}>
              <Table sx={{ minWidth: 650 }} size="small" tableLayout="fixed">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: pTheme => pTheme.palette.secondary.dark
                      }}
                    />
                    <TableCell align="right">Meses Positivos</TableCell>
                    <TableCell align="right">Meses Negativos</TableCell>
                    <TableCell align="right">Maior Retorno</TableCell>
                    <TableCell align="right">Menor Retorno</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {xSeries.map((xSerie, xSerieIndex) => {
                    const xResultado = xSerie.resultado;
                    const xAtivoDescricao = utils.helpers.getAtivoDescricao(xSerie.raw);
                    const xAtivoLabel = isUpSm
                      ? xAtivoDescricao
                      : utils.typo.shortname(xAtivoDescricao, 25);
                    return (
                      <TableRow key={xSerie.ativo}>
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{
                            position: 'sticky',
                            left: 0,
                            zIndex: 1,
                            backgroundColor: pTheme => pTheme.palette.secondary.dark
                          }}>
                          <Stack
                            direction="row"
                            gap={1}
                            alignItems="center"
                            sx={{ '& img': { display: 'block' } }}>
                            {xSerie?.raw?.logo_pessoa_id && (
                              <PessoaImage
                                logoPessoaId={xSerie.raw.logo_pessoa_id}
                                alt={xSerie.raw.pessoa}
                                hasLogo={xSerie.raw.logo_ok}
                                size={18}
                              />
                            )}
                            <Stack>
                              <Typography
                                data-testid={`rentabilidade-resumo-${xSerie.ativo}-value`}
                                variant="body2"
                                color="textPrimary"
                                sx={{ fontWeight: 500 }}
                                noWrap>
                                {xAtivoLabel}
                              </Typography>
                            </Stack>
                          </Stack>
                        </TableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={
                            highlightEnabled && xBestIndexes.mesesPositivos.includes(xSerieIndex)
                          }>
                          {xResultado ? (
                            <Typography variant="caption">
                              {xResultado.mesesPositivos} ({xResultado.percentualMesesPositivos})
                            </Typography>
                          ) : (
                            pvSemDado()
                          )}
                        </MelhorValorTableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={
                            highlightEnabled && xBestIndexes.mesesNegativos.includes(xSerieIndex)
                          }>
                          {xResultado ? (
                            <Typography variant="caption">
                              {xResultado.mesesNegativos} ({xResultado.percentualMesesNegativos})
                            </Typography>
                          ) : (
                            pvSemDado()
                          )}
                        </MelhorValorTableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={
                            highlightEnabled && xBestIndexes.maiorRetorno.includes(xSerieIndex)
                          }>
                          {typeof xResultado?.maiorRetorno === 'number' &&
                          Number.isFinite(xResultado.maiorRetorno) ? (
                            <Typography
                              variant="caption"
                              color={xResultado.maiorRetorno < 0 && 'error.light'}>
                              {xResultado.maiorRetorno.toFixed(2)}%
                            </Typography>
                          ) : (
                            pvSemDado()
                          )}
                        </MelhorValorTableCell>
                        <MelhorValorTableCell
                          align="right"
                          isBest={
                            highlightEnabled && xBestIndexes.menorRetorno.includes(xSerieIndex)
                          }>
                          {typeof xResultado?.menorRetorno === 'number' &&
                          Number.isFinite(xResultado.menorRetorno) ? (
                            <Typography
                              variant="caption"
                              color={xResultado.menorRetorno < 0 && 'error.light'}>
                              {xResultado.menorRetorno.toFixed(2)}%
                            </Typography>
                          ) : (
                            pvSemDado()
                          )}
                        </MelhorValorTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});

export default AtivoConsistencia;
